import React, {
  useEffect,
  useState,
  useRef,
  useMemo,
  useCallback,
} from 'react';
import { useFieldArray, UseFormReturn, useWatch } from 'react-hook-form';
import { useDebounce } from '@features/onboarding/hooks/useDebounce';
import { HealthQuestion } from '@features/onboarding/components/6-health-question-form/interfaces';
import { FormField, FormItem, FormControl } from '@components/ui/form';
import { useHealthStore } from 'src/store/health';
import { MedicationSearchResult } from 'src/services/interfaces/health';
import { useOnboardingStore } from '@features/onboarding/store/onboarding';
// import { t } from '@/i18n/index';
import { MedicationInput } from './MedicationInput';
import { MedicationList } from './MedicationList';
import { Trans } from 'react-i18next';

interface MedicationQuestionStepProps {
  question: HealthQuestion;
  form: UseFormReturn<any>;
  onMedicationsChange?: (medications: any[]) => void;
}

export const MedicationQuestionStep: React.FC<MedicationQuestionStepProps> = ({
  question,
  form,
  onMedicationsChange,
}) => {
  const { control, getValues, setValue } = form;
  const { fields, append, prepend, remove } = useFieldArray({
    control,
    name: `healthQuestions.${question.id}.medications` as any,
  });

  // Watch the medications array for changes
  const watchedMedications = useWatch({
    control,
    name: `healthQuestions.${question.id}.medications`,
    defaultValue: [],
  });

  const {
    getMedications,
    isLoading,
    setError,
    error,
    isFailValidation,
    setFailValidation,
  } = useHealthStore();
  const [inputValue, setInputValue] = useState<string>('');
  const [suggestions, setSuggestions] = useState<MedicationSearchResult[]>([]);
  const abortControllerRef = useRef<AbortController | null>(null);
  const lastRequestedValueRef = useRef<string>('');
  // Store brand/generic info keyed by medication API id — survives RHF re-renders
  const brandGenericMapRef = useRef<
    Record<
      string,
      {
        brandName: string;
        brandId: string;
        genericName: string;
        genericId: string;
        originalType: 'brand' | 'generic';
      }
    >
  >({});
  const debouncedInputValue = useDebounce(inputValue, 500);
  const applicants = useOnboardingStore((state) => state.data?.applicants) || [];

  // Fetch suggestions when debouncedInputValue changes
  useEffect(() => {
    // Abort previous request if exists
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Skip if value is too short or same as last requested
    if (!debouncedInputValue || debouncedInputValue.length < 3) {
      setSuggestions([]);
      lastRequestedValueRef.current = '';
      return;
    }

    // Skip if we already requested this value
    if (lastRequestedValueRef.current === debouncedInputValue) {
      return;
    }

    // Mark this value as requested
    lastRequestedValueRef.current = debouncedInputValue;

    // Create new abort controller for this request
    abortControllerRef.current = new AbortController();
    const currentAbortController = abortControllerRef.current;

    const fetchSuggestions = async () => {
      try {
        const data = await getMedications(debouncedInputValue, currentAbortController.signal);
        
        // Check if request was aborted
        if (currentAbortController.signal.aborted) {
          return;
        }

        // Get IDs of already selected medications from watchedMedications (real form data)
        const existingIds = new Set<string>();
        if (watchedMedications && Array.isArray(watchedMedications)) {
          watchedMedications.forEach((med: any) => {
            if (med?.id) {
              existingIds.add(med.id);
            }
          });
        }

        // Build list that excludes already-added medications by id AND removes duplicates within the suggestions themselves
        const seenIds = new Set<string>();
        const uniqueSuggestions = [] as typeof data;
        for (const item of data) {
          // Skip if already in form (check by id)
          if (existingIds.has(item.id)) continue;
          // Skip if duplicate in suggestions list
          if (seenIds.has(item.id)) continue;
          seenIds.add(item.id);
          uniqueSuggestions.push(item);
        }

        // Only update suggestions if they actually changed
        setSuggestions((prev) => {
          const prevStr = JSON.stringify(prev);
          const newStr = JSON.stringify(uniqueSuggestions);
          return prevStr === newStr ? prev : uniqueSuggestions;
        });
      } catch (err: unknown) {
        // Don't handle errors for aborted requests
        if (err && (err as any).name !== 'AbortError' && (err as any).code !== 'ERR_CANCELED') {
          console.error('Failed to fetch suggestions:', err);
          // Reset last requested value on error so we can retry
          if (lastRequestedValueRef.current === debouncedInputValue) {
            lastRequestedValueRef.current = '';
          }
        }
      } finally {
        // Clear abort controller reference if it's still the current one
        if (abortControllerRef.current === currentAbortController) {
          abortControllerRef.current = null;
        }
      }
    };

    fetchSuggestions();

    return () => {
      if (abortControllerRef.current === currentAbortController) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
    };
  }, [debouncedInputValue, watchedMedications, getMedications]);

  useEffect(() => {
    setFailValidation(false);
  }, [inputValue, setInputValue]);

  // Track previous fields length to detect new additions
  const prevFieldsLengthRef = useRef<number>(fields.length);
  
  // Ensure newly added medications have clean default values
  // This prevents react-hook-form from inheriting values from previous fields
  useEffect(() => {
    // Only process if a new medication was added (length increased)
    if (fields.length > prevFieldsLengthRef.current) {
      // New medication was added at index 0 (because we use prepend)
      const newIndex = 0;
      const fieldName = `healthQuestions.${question.id}.medications.${newIndex}`;
      
      // Explicitly reset all fields for the newly added medication
      // Use setTimeout to ensure prepend has completed and form state is updated
      setTimeout(() => {
        const medData = getValues(`${fieldName}`);
        setValue(`${fieldName}.dosageId`, undefined, { shouldDirty: false, shouldValidate: false });
        setValue(`${fieldName}.dosageValue`, '', { shouldDirty: false, shouldValidate: false });
        setValue(`${fieldName}.dosageDescription`, '', { shouldDirty: false, shouldValidate: false });
        setValue(`${fieldName}.frequency`, '', { shouldDirty: false, shouldValidate: false });
        setValue(`${fieldName}.frequencyDescription`, '', { shouldDirty: false, shouldValidate: false });
        setValue(`${fieldName}.description`, '', { shouldDirty: false, shouldValidate: false });
        setValue(`${fieldName}.isNew`, true, { shouldDirty: false, shouldValidate: false });
        // Preserve medication type fields for brand/generic detection
        if (medData?.medicationType) {
          setValue(`${fieldName}.medicationType`, medData.medicationType, { shouldDirty: false, shouldValidate: false });
          setValue(`${fieldName}.genericName`, medData.genericName || null, { shouldDirty: false, shouldValidate: false });
          setValue(`${fieldName}.genericId`, medData.genericId || null, { shouldDirty: false, shouldValidate: false });
          if (medData.brandName) {
            setValue(`${fieldName}.brandName`, medData.brandName, { shouldDirty: false, shouldValidate: false });
            setValue(`${fieldName}.brandId`, medData.brandId, { shouldDirty: false, shouldValidate: false });
          }
          if (medData._bgKey) {
            setValue(`${fieldName}._bgKey`, medData._bgKey, { shouldDirty: false, shouldValidate: false });
          }
        }
      }, 0);
    }
    
    // Update ref to current length
    prevFieldsLengthRef.current = fields.length;
  }, [fields.length, question.id, setValue]);

  // On mount, restore brand/generic info for existing medications
  // that were loaded from saved application data (no _bgKey yet)
  const bgRestoredRef = useRef(false);
  useEffect(() => {
    if (bgRestoredRef.current) return;
    if (!watchedMedications || watchedMedications.length === 0) return;

    // First, restore brandGenericMapRef from medications that already have
    // _bgKey and saved brand/generic fields (e.g. when navigating back via "previous")
    watchedMedications.forEach((med: any) => {
      if (
        med?._bgKey &&
        med?.brandName &&
        med?.brandId &&
        med?.genericName &&
        med?.genericId &&
        !brandGenericMapRef.current[med._bgKey]
      ) {
        brandGenericMapRef.current[med._bgKey] = {
          brandName: med.brandName,
          brandId: med.brandId,
          genericName: med.genericName,
          genericId: med.genericId,
          originalType: 'brand',
        };
      }
    });

    const medsToResolve = watchedMedications.filter(
      (med: any) => med?.id && !med?._bgKey
    );
    if (medsToResolve.length === 0) {
      bgRestoredRef.current = true;
      return;
    }

    bgRestoredRef.current = true;

    medsToResolve.forEach(async (med: any, idx: number) => {
      try {
        const results = await getMedications(med.name || '', undefined);
        // Find exact match by id
        const match = results.find((r) => r.id === med.id);
        // Also check if this med is actually a generic that was switched from a brand
        const brandMatch = !match
          ? results.find(
              (r) =>
                r.medication_type === 'brand' &&
                r.generic_id === med.id
            )
          : null;

        const source = match || brandMatch;
        if (!source) return;

        const isBrand =
          source.medication_type === 'brand' &&
          !!source.generic_name &&
          !!source.generic_id;

        // If the matched result is brand with generic, populate the map
        if (isBrand) {
          const key = `brand_${source.id}`;
          brandGenericMapRef.current[key] = {
            brandName: source.name,
            brandId: source.id,
            genericName: source.generic_name!,
            genericId: source.generic_id!,
            originalType: 'brand',
          };
          // Find actual form index for this medication
          const formIdx = watchedMedications.findIndex(
            (m: any) => m?.id === med.id
          );
          if (formIdx !== -1) {
            const fieldName = `healthQuestions.${question.id}.medications.${formIdx}`;
            setValue(`${fieldName}._bgKey`, key, {
              shouldDirty: false,
              shouldValidate: false,
            });
            // Also set medicationType if not set
            if (!med.medicationType || med.medicationType === '') {
              const isCurrentlyGeneric = med.id === source.generic_id;
              setValue(
                `${fieldName}.medicationType`,
                isCurrentlyGeneric ? 'generic' : 'brand',
                { shouldDirty: false, shouldValidate: false }
              );
            }
          }
        } else if (
          brandMatch &&
          brandMatch.medication_type === 'brand' &&
          brandMatch.generic_name &&
          brandMatch.generic_id
        ) {
          // Med was added as generic but has a brand counterpart -
          // don't show choice since user explicitly picked generic
        }
      } catch {
        // Silently fail - medication info just won't have brand/generic choice
      }
    });
  }, [watchedMedications, getMedications, question.id, setValue]);

  const dynamicRecipientOptions = useMemo(
    () =>
      (
        [
          { value: 'primary', label: 'You' },
          ...applicants
            .filter((dep: any) => dep.applicant === 'spouse')
            .map((dep: any, i: number) => ({
              value: `spouse_${i}`,
              label: dep.firstName || 'Spouse',
            })),
          ...applicants
            .filter((dep: any) => dep.applicant === 'dependent')
            .map((dep: any, idx: number) => ({
              value: `dependent_${idx}`,
              label: dep.firstName || `Dependent ${idx + 1}`,
            })),
        ] as { value: string; label: string }[]
      ).map((v, index) => ({
        value: `${index}`,
        label: v.label,
      })),
    [applicants]
  );

  const addSuggestion = useCallback(
    (suggestion: MedicationSearchResult) => {
      // Check if medication is already added by checking watchedMedications (real form data)
      const isAlreadyAdded = watchedMedications?.some(
        (med: any) => med?.id === suggestion.id
      );

      if (isAlreadyAdded) {
        return;
      }

      const defaultRecipient =
        dynamicRecipientOptions.length === 1
          ? dynamicRecipientOptions[0].value
          : '';

      const isBrandWithGeneric = suggestion.medication_type === 'brand' && !!suggestion.generic_name && !!suggestion.generic_id;

      // Store brand/generic info keyed by brand ID only
      // Generic medications selected directly from autocomplete should NOT show the choice
      if (isBrandWithGeneric) {
        const info = {
          brandName: suggestion.name,
          brandId: suggestion.id,
          genericName: suggestion.generic_name!,
          genericId: suggestion.generic_id!,
          originalType: 'brand' as const,
        };
        // Key by brand id AND generic id so lookup works after switching
        // But use a unique key per "addition" to avoid collisions with directly-added generics
        const additionKey = `brand_${suggestion.id}`;
        brandGenericMapRef.current[additionKey] = info;
      }

      const medicationData = {
        id: suggestion.id,
        name: suggestion.name,
        dosageId: undefined,
        dosageValue: '',
        dosageDescription: '',
        frequency: '',
        frequencyDescription: '',
        applicant: defaultRecipient,
        description: '',
        isNew: true,
        medicationType: suggestion.medication_type || 'generic',
        genericName: suggestion.generic_name || null,
        genericId: suggestion.generic_id || null,
        // Store key for brand/generic map lookup and original brand info
        ...(isBrandWithGeneric ? {
          brandName: suggestion.name,
          brandId: suggestion.id,
          _bgKey: `brand_${suggestion.id}`,
        } : {}),
      };
      
      // Add medication at index 0 (prepend)
      // Fields will be reset in useEffect when fields.length changes
      prepend(medicationData, { shouldFocus: false });
    },
    [watchedMedications, dynamicRecipientOptions, prepend]
  );

  return (
    <>
      <FormField
        name={`healthQuestions.${question.id}.medications` as any}
        control={control}
        render={({ field }) => (
          <FormItem className="relative mt-10 gap-0">
            <FormControl>
              <>
                <MedicationInput
                  field={field}
                  inputValue={inputValue}
                  suggestions={suggestions}
                  isLoading={isLoading}
                  error={error}
                  setInputValue={setInputValue}
                  setError={setError}
                  setSuggestions={setSuggestions}
                  addSuggestion={addSuggestion}
                />
                {isFailValidation && !watchedMedications?.some((m: any) => m?.id) && (
                  <p className="text-error font-semibold mt-4 text-center xl:text-left">
                    <Trans i18nKey="errors.specifyAnyMedication" />
                  </p>
                )}
              </>
            </FormControl>
          </FormItem>
        )}
      />
      {fields.length > 0 && watchedMedications?.some((m: any) => m?.id) && (
        <div className="mt-4">
          Add another medication by searching above, or click{' '}
          <strong>Next</strong> to continue.
        </div>
      )}
      <MedicationList
        fields={fields}
        control={control}
        questionId={question.id}
        dynamicRecipientOptions={dynamicRecipientOptions}
        remove={remove}
        append={append}
        brandGenericMap={brandGenericMapRef.current}
      />
    </>
  );
};
